import { LIMIT } from "@/constants";
import { useGetExpenseListQuery } from "@/features/expense/api";
import { DynamicPurchasedOrders } from "@/features/expense/component/DynamicPurchaseOrder";
import {
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useState, ChangeEvent, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown, Plus, SearchIcon } from "lucide-react";
import styles from "../css/Expense.module.css"
export const Route = createFileRoute(
  "/(app)/(store)/expense/$expenseCategoryId/"
)({
  component: ExpenseScreen,
});

function ExpenseScreen() {
  const { expenseCategoryId } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const expenseCategory = location.state?.expenseCategory;

  const [page, setPage] = useState<number>(1);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");

  // Ref for intersection observer
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Debounce search (500ms delay)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // reset page when searching
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const { data = [], isFetching } = useGetExpenseListQuery(
    {
      ["include-last-order"]: true,
      category: expenseCategory._id,
      page,
      limit: LIMIT * 2,
    },
    {
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    }
  );

  function handleNavigation() {
    navigate({
      to: `/expense/${expenseCategoryId}/create`,
      state: { expenseCategory },
    });
  }

  const handlePressOnPurchasedOrders = (item: any) => {
    console.log({ item }, "item");
    navigate({
      to: `/expense/${expenseCategoryId}/${item._id}`,
      state: { expenseCategory, expenseOrder: item },
    });
  };

  // ✅ Subcategory filter
  let filteredData = selectedSubCategory
    ? data.filter(
        (expense: any) =>
          expense.serviceId?.subCategoryId === selectedSubCategory
      )
    : data;

  // ✅ Search filter
  if (debouncedSearch.trim()) {
    filteredData = filteredData.filter((expense: any) =>
      expense?.sellerUserId?.name
        ?.toLowerCase()
        .includes(debouncedSearch.toLowerCase())
    );
  }

  // ✅ Infinite Scroll: Observe bottom div
  useEffect(() => {
    if (!loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && !isFetching) {
          // only load more if current data size = expected page size
          if (data.length === page * LIMIT * 2) {
            setPage((prev) => prev + 1);
          }
        }
      },
      { threshold: 1.0 }
    );

    observer.observe(loadMoreRef.current);

    return () => {
      if (loadMoreRef.current) observer.unobserve(loadMoreRef.current);
    };
  }, [data, page, isFetching]);

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        {/* Header */}
        <div className={styles.headerOuter}>
          <div className={styles.header}>
            <h2 className={styles.title}>{expenseCategory?.name}</h2>

            <div className={styles.actions}>
              {/* Search */}
              <div className={styles.searchContainer}>
                <SearchIcon className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setSearchTerm(e.target.value)
                  }
                  className={styles.searchInput}
                />
              </div>

              {/* Subcategory Dropdown */}
              <div className={styles.dropdownContainer}>
                <select
                  value={selectedSubCategory}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setSelectedSubCategory(e.target.value)
                  }
                  className={styles.dropdownSelect}
                >
                  <option value="">Category</option>
                  {expenseCategory?.subCategoryList?.map((sub: any) => (
                    <option key={sub._id} value={sub._id}>
                      {sub.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className={styles.dropdownIcon} />
              </div>

              {/* Add New Button */}
              <Button onClick={handleNavigation} className={styles.addButton}>
                <Plus />
                Add New
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div>
          <div className={styles.list}>
            {filteredData?.map((expense: any) => (
              <DynamicPurchasedOrders
                key={expense._id}
                item={expense}
                category={expenseCategory?.name}
                products={expense?.lastOrderList?.at(0)?.itemList}
                totalAmount={expense.totalAmount}
                status={expense.lastOrderList?.at(0)?.payment?.paymentStatus}
                onPress={() => handlePressOnPurchasedOrders(expense)}
              />
            ))}

            {filteredData.length === 0 && !isFetching && (
              <div className={styles.emptyState}>
                No {expenseCategory?.name} found.
              </div>
            )}

            {/* Infinite scroll target */}
            <div ref={loadMoreRef} className={styles.loadMore}></div>
          </div>

          {/* Loading State */}
          {isFetching && page === 1 && (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
